<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { marked } from 'marked';
import { $fetch } from '@@home/plugins/fetch';
import { useAppStore } from '@@home/stores/app';
import { useAsyncData } from '@@home/hooks/async-data';
import CountTo from '@@home/components/CountTo.vue';
import { statsPv } from '@@home/utils';

import defaultProjectBanner from '@@home/assets/project/header.jpg';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
});

const router = useRouter();
const appStore = useAppStore();

marked.use({
  gfm: true,
  breaks: false,
});

const { data: detail } = useAsyncData<Record<string, any>>(
  'project:detail',
  () => $fetch('/project/v1/info', { data: { id: props.id } }),
  {
    cache: false,
    watch: [() => props.id],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (serverContext) {
        if (handleSuccess && detail.value?.id) {
          serverContext.title = detail.value.title;
          if (detail.value.intro) {
            serverContext.description = detail.value.intro;
          }
          else {
            serverContext.description = `${detail.value.projectValue}; ${detail.value.techFeatures}`;
          }
          serverContext.keywords = detail.value.label?.join?.(', ');
        }
        else {
          // 如果请求出错直接服务器重定向到 404 页面
          serverContext.redirect = '/404';
        }
      }
      else {
        if (handleSuccess && detail.value?.id) {
          appStore.setTitle(detail.value.title);

          // 统计埋点: 因标题是异步获取的，所以 pv 要延迟统计
          statsPv();
        }
        else {
          router.replace('/404');
        }
      }
    },
  },
);

const markedContent = computed(() => {
  return detail.value?.content ? marked.parse(detail.value.content) : '';
});

const isAllNumberEmpty = computed(() => {
  return (
    Number(detail.value.commits) == 0
    && Number(detail.value.star) == 0
    && Number(detail.value.contributor) == 0
    && Number(detail.value.watch) == 0
    && Number(detail.value.fork) == 0
    && Number(detail.value.pr) == 0
  );
});
</script>

<template>
  <main class="page-project-detail">
    <div
      class="project-detail"
      :style="{
        backgroundImage: detail ? `url(${detail?.detailBanner || defaultProjectBanner})` : 'none',
        color: detail?.color || '#fff',
      }"
    >
      <div class="head-img container" v-if="detail">
        <div class="left-characters max-w-750px <2xl:max-w-650px">
          <h1>{{ detail.title }}</h1>
          <p>{{ detail.projectValue }}</p>
          <span>{{ detail.intro }}</span>
          <ul class="m-0 mt-[16px] flex list-none p-0">
            <li v-if="detail.projectDownload">
              <a :href="detail.projectDownload" class="block color-inherit no-underline" target="_blank">
                <img src="../../assets/project/download.svg" :alt="$t('projects.detail.title1')">
                <p>{{ $t('projects.detail.title1') }}</p>
              </a>
            </li>
            <li v-if="detail.projectCode">
              <a :href="detail.projectCode" class="block color-inherit no-underline" target="_blank">
                <img src="../../assets/project/code.svg" :alt="$t('projects.detail.title2')">
                <p>{{ $t('projects.detail.title2') }}</p>
              </a>
            </li>
            <li v-if="detail.externalLink">
              <a :href="detail.externalLink" class="block color-inherit no-underline" target="_blank">
                <img src="../../assets/project/community.svg" :alt="$t('projects.detail.title3')">
                <p>{{ $t('projects.detail.title3') }}</p>
              </a>
            </li>
            <li v-if="detail.projectExtInfo?.additionalLinkText && detail.projectExtInfo?.additionalLinkImage && detail.projectExtInfo?.additionalLinkUrl">
              <a :href="detail.projectExtInfo.additionalLinkUrl" class="block color-inherit no-underline" target="_blank">
                <img :src="detail.projectExtInfo.additionalLinkImage" :alt="detail.projectExtInfo.additionalLinkText" class="object-cover">
                <p>{{ detail.projectExtInfo.additionalLinkText }}</p>
              </a>
            </li>
          </ul>
        </div>
      <!-- <div class="right-image">
            <img src="@ssr/home/assets/home/bottom-banner-bg.jpg" alt="">
          </div> -->
      </div>
    </div>

    <div class="main-content container">
      <template v-if="detail">
        <div class="project-details-header" :class="{ empty: isAllNumberEmpty }">
          <div class="main-content-image" v-if="detail.detailLogo">
            <img :src="detail.detailLogo" :alt="detail.title">
          </div>
          <ul class="project-details-second" v-if="!isAllNumberEmpty">
            <li v-if="detail.commits !== '' && !isNaN(detail.commits)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.commits)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number1') }}
              </div>
            </li>
            <li v-if="detail.star !== '' && !isNaN(detail.star)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.star)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number2') }}
              </div>
            </li>
            <li v-if="detail.contributor !== '' && !isNaN(detail.contributor)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.contributor)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number3') }}
              </div>
            </li>
            <li v-if="detail.watch !== '' && !isNaN(detail.watch)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.watch)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number4') }}
              </div>
            </li>
            <li v-if="detail.fork !== '' && !isNaN(detail.fork)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.fork)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number5') }}
              </div>
            </li>
            <li v-if="detail.pr !== '' && !isNaN(detail.pr)">
              <div class="top-number">
                <CountTo :end-val="Number(detail.pr)" />
              </div>
              <div class="bottom-text">
                {{ $t('projects.detail.number6') }}
              </div>
            </li>
          </ul>
        </div>

        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="project-details-content" v-html="markedContent"></div>
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.project-detail {
  min-height: 400px;
  color: #fff;
  background-position: center;
  background-size: cover;

  .head-img {
    display: flex;
    justify-content: space-between;
    box-sizing: border-box;
    padding-top: 60px;
    padding-bottom: 60px;

    .left-characters {
      h1 {
        margin: 0;
        font-weight: 500;
        font-size: 50px;
      }

      p {
        margin-top: 10px;
        margin-bottom: 0;
        font-weight: 400;
        font-size: 24px;
        line-height: 1.4;
      }

      span {
        display: block;
        margin-top: 24px;
        font-weight: 400;
        font-size: 16px;
        line-height: 1.6;
      }

      ul {
        li {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 82px;
          height: 78px;
          margin-right: 24px;
          text-align: center;

          img {
            width: 56px;
            height: 56px;
          }

          p {
            margin-top: 0;
            font-weight: 400;
            font-size: 16px;
          }
        }
      }
    }
  }
}

.main-content {
  min-height: 1000px;
  margin-top: 48px;

  .project-details-header {
    display: flex;
    height: 160px;
    background-color: rgba(45, 45, 45, 0.02);
    box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.15);

    &.empty {
      height: auto;
      background-color: transparent;
      box-shadow: none;

      .main-content-image {
        border-radius: 4px;
      }
    }

    .main-content-image {
      flex-shrink: 0;
      width: 368px;
      border-radius: 4px 0 0 4px;
      box-shadow: 0 0 6px 0 rgba(0, 0, 0, 0.15);

      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .project-details-second {
      display: flex;
      flex-grow: 1;
      justify-content: space-between;
      margin: 0;
      padding: 0;
      list-style: none;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;

      li {
        flex-basis: 0;
        flex-grow: 1;
        align-self: center;
        color: #2d2d2d;
        text-align: center;

        .top-number {
          font-weight: 500;
          font-size: 32px;
        }

        .bottom-text {
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
        }
      }
    }
  }

  .project-details-content {
    box-sizing: border-box;
    margin-top: 48px;
    padding-bottom: 160px;
    color: #2d2d2d;
    white-space: wrap;
    word-wrap: break-word;

    &:deep() {
      h1,
      h2 {
        font-weight: 500;
        font-size: 24px;
      }

      p {
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
      }

      img {
        display: block;
        max-width: 100%;
        object-fit: contain;
      }
    }
  }
}
</style>
